/**
 * Submit an affinity request. The API scores it on a cache miss (Qdrant
 * vectors + Neo4j history) and returns the document in the same response.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { type AffinityDocument, type AffinityScoreRequest, postAffinity } from '../services/api';

export type AffinityPhase = 'idle' | 'loading' | 'ready' | 'error';

interface AffinityState {
  phase: AffinityPhase;
  document: AffinityDocument | null;
  error: string | null;
}

export function useAffinity() {
  const [state, setState] = useState<AffinityState>({ phase: 'idle', document: null, error: null });
  const runId = useRef(0);

  useEffect(
    () => () => {
      runId.current += 1;
    },
    []
  );

  const submit = useCallback(async (request: AffinityScoreRequest) => {
    const current = ++runId.current;
    setState({ phase: 'loading', document: null, error: null });
    try {
      const document = await postAffinity(request);
      if (current === runId.current) setState({ phase: 'ready', document, error: null });
    } catch (err) {
      if (current !== runId.current) return;
      const message = err instanceof Error ? err.message : String(err);
      setState({ phase: 'error', document: null, error: message });
    }
  }, []);

  const reset = useCallback(() => {
    runId.current += 1;
    setState({ phase: 'idle', document: null, error: null });
  }, []);

  return { ...state, submit, reset };
}
